import * as React from 'react';
import { ChevronRight } from 'lucide-react';
import { Link } from '@tanstack/react-router';
import { Button } from '@/components/ui/button';
import { Spinner } from '@/components/ui/spinner';
import { ErrorMessage } from '@/components/ui/error-message';
import { KpiSourcePill } from '@/components/ui/kpi-source-pill';
import { ChartLegend, UNIT_SWATCH, UnitSquares, unitRun } from '@/components/ui/charts';
import { CoverageMeter } from '@/components/discovery/coverage-bits';
import { plural, sourcesRuleText } from '@/components/discovery/discovery-logic';
import { ApiError } from '@/lib/api/client';
import {
  useCoverageQuery,
  useDiscoverySummaryQuery,
  type DiscoverySummary,
} from '@/lib/api/discovery';
import { sessionsToSchedule } from './overview-plan';
import { OVERVIEW_LINK } from './overview-go';

/**
 * Discovery on the engagement Overview, drawn (5 Nov 2026): the sessions
 * as one square each (held, scheduled, not scheduled yet), and each area
 * with its source meter, the Discovery screens' own `CoverageMeter`. An
 * area opens its findings. Every number is the API's (the summary's KPIs
 * and the coverage read); nothing is counted here but the sessions not yet
 * given a date, the plan less the held and the scheduled.
 *
 * Shown only when the engagement's Discovery module is on.
 */
export function DiscoveryStatusCard({ engagementId }: { engagementId: string }) {
  const q = useDiscoverySummaryQuery(engagementId);

  return (
    <section
      className="rounded-card border border-ink-100 bg-paper-0 p-card-pad"
      aria-labelledby="discovery-status-title"
      data-testid="discovery-status-card"
    >
      <div className="mb-4 flex items-start justify-between gap-3">
        <div>
          <h2 id="discovery-status-title" className="t-h3">
            {q.data?.framework
              ? `Discovery · ${q.data.framework.name} v${q.data.framework.version}`
              : 'Discovery'}
          </h2>
          <p className="t-meta">{caption(q.data)}</p>
        </div>
        <Button asChild variant="ghost" size="sm">
          <Link to="/engagements/$engagementId/discovery" params={{ engagementId }}>
            Open Discovery
            <ChevronRight className="size-3" aria-hidden="true" />
          </Link>
        </Button>
      </div>

      {q.error ? (
        q.error instanceof ApiError && q.error.code === 'not_found' ? (
          <p className="text-body-sm text-ink-500">
            Discovery is not available here: the module is off for this engagement, or you do not
            have access to it.
          </p>
        ) : (
          <ErrorMessage error={q.error} />
        )
      ) : !q.data ? (
        <p className="flex items-center gap-2 text-body-sm text-ink-500">
          <Spinner /> Loading discovery…
        </p>
      ) : !q.data.started ? (
        <NotStarted engagementId={engagementId} />
      ) : (
        <Body engagementId={engagementId} s={q.data} />
      )}
    </section>
  );
}

function caption(s: DiscoverySummary | undefined): string {
  if (!s) return 'Interviews, documents and the evidence they confirm';
  if (!s.started) return 'Not started';
  return `${plural(s.kpis.areas, 'area')} · ${sourcesRuleText(s)}`;
}

function NotStarted({ engagementId }: { engagementId: string }) {
  return (
    <div className="flex flex-wrap items-center justify-between gap-3">
      <p className="text-body-sm text-ink-500">
        Discovery has not started. Choose the framework, check the client facts and name who you
        will interview.
      </p>
      <Button asChild variant="outline" size="sm">
        <Link to="/engagements/$engagementId/discovery/setup" params={{ engagementId }}>
          Set up Discovery
        </Link>
      </Button>
    </div>
  );
}

const BLOCK_HEAD = 'mb-2 flex items-baseline justify-between gap-3';
const BLOCK_TITLE = 'text-body-sm font-semibold text-ink-900';

function Body({ engagementId, s }: { engagementId: string; s: DiscoverySummary }) {
  const k = s.kpis;
  const params = { engagementId };
  const toSchedule = sessionsToSchedule(k);
  const coverage = useCoverageQuery(engagementId);
  const fmt = (v: number) => v.toLocaleString('en-US');

  return (
    <div className="flex flex-col gap-4">
      <div>
        <div className={BLOCK_HEAD}>
          <h3 className={BLOCK_TITLE}>Sessions</h3>
          <Link
            to="/engagements/$engagementId/discovery/sessions"
            params={params}
            className={OVERVIEW_LINK}
          >
            Sessions
          </Link>
        </div>
        {k.interviews_total === 0 ? (
          <p className="text-body-sm text-ink-500">No sessions are planned yet.</p>
        ) : (
          <>
            <UnitSquares
              label={`${fmt(k.interviews_held)} held, ${fmt(k.interviews_scheduled)} scheduled, ${fmt(toSchedule)} not scheduled`}
              units={[
                ...unitRun('held', k.interviews_held, 'healthy', 'Held'),
                ...unitRun('scheduled', k.interviews_scheduled, 'amber', 'Scheduled'),
                ...unitRun('open', toSchedule, 'empty', 'Not scheduled yet'),
              ]}
            />
            <ChartLegend
              className="mt-2.5"
              items={[
                { color: UNIT_SWATCH.healthy, label: <Count n={k.interviews_held}>Held</Count> },
                ...(k.interviews_scheduled > 0
                  ? [
                      {
                        color: UNIT_SWATCH.amber,
                        label: <Count n={k.interviews_scheduled}>Scheduled</Count>,
                      },
                    ]
                  : []),
                ...(toSchedule > 0
                  ? [
                      {
                        color: UNIT_SWATCH.empty,
                        label: <Count n={toSchedule}>Not scheduled</Count>,
                      },
                    ]
                  : []),
              ]}
            />
          </>
        )}
      </div>

      <div className="border-t border-ink-100 pt-4">
        <div className={BLOCK_HEAD}>
          <h3 className={BLOCK_TITLE}>Sources per area</h3>
          <Link
            to="/engagements/$engagementId/discovery/findings"
            params={params}
            search={{ area: 'all' }}
            className={OVERVIEW_LINK}
          >
            Findings
          </Link>
        </div>
        {coverage.error ? (
          <ErrorMessage error={coverage.error} />
        ) : !coverage.data ? (
          <p className="flex items-center gap-2 text-body-sm text-ink-500">
            <Spinner /> Loading areas…
          </p>
        ) : (
          <ul className="m-0 grid list-none grid-cols-1 gap-x-7 p-0 sm:grid-cols-2">
            {coverage.data.areas.map((a) => (
              <li key={a.area_id}>
                <Link
                  to="/engagements/$engagementId/discovery/findings"
                  params={params}
                  search={{ area: a.area_id }}
                  title={a.status_label}
                  className="flex items-center gap-2 border-b border-ink-100 py-1.5 text-body-sm text-ink-900 hover:text-accent-amber-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent-amber-600"
                >
                  <span className="min-w-0 flex-1 truncate">{a.label}</span>
                  <CoverageMeter sources={a.sources} required={a.required} status={a.status} />
                </Link>
              </li>
            ))}
          </ul>
        )}
        <ChartLegend
          className="mt-2.5"
          items={[
            { color: 'bg-posture-healthy', label: <Count n={k.corroborated}>Covered</Count> },
            { color: 'bg-posture-at-risk', label: <Count n={k.partial}>Needs more sources</Count> },
            { color: 'bg-ink-100', label: <Count n={k.none}>No evidence yet</Count> },
          ]}
        />
      </div>

      <KpiSourcePill source="From the session plan and confirmed evidence" className="self-start" />
    </div>
  );
}

function Count({ n, children }: { n: number; children: React.ReactNode }) {
  return (
    <span>
      {children}{' '}
      <b className="font-semibold tabular-nums text-ink-900">{n.toLocaleString('en-US')}</b>
    </span>
  );
}
